import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ChargePointFormModel } from 'src/core/model/charge-point.model';
import { removeEmptyValues } from 'src/shared/form/remove-empty-values';
import { TextField } from 'src/shared/form/text-field';
import { CancelButton } from 'src/shared/button/cancel.button';
import { SubmitButton } from 'src/shared/button/submit.button';
import { LocationModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/ocpi/src/protocol/v2.1.1/models/location.model';
import { EvseModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/ocpi/src/protocol/v2.1.1/models/evse.model';
import { CpoModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/cpos/cpo.model';
import { CreateChargePointModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/cpo-service/charge-point/models/create-charge-point.model';
import { Checkbox, FormControlLabel } from '@mui/material';
import {
  ChargePointFormValidation,
  useChargePointFormValidation,
} from './use-charge-point.form-validation';
import { CpoSelect } from './cpo-select';
import { LocationSelect } from './location-select';
import { EvseSelect } from './evse-select';
import { ConnectorSelect } from './connector-select';

type ChargePointFormViewProps = {
  readonly formType: 'create' | 'update';
  readonly defaultValues?: ChargePointFormModel;
  readonly pending: boolean;
  readonly onSubmit: (payload: ChargePointFormModel) => void;
  readonly onCancel: () => void;
};

export function ChargePointFormView(
  props: ChargePointFormViewProps,
): React.JSX.Element {
  const { formType, defaultValues, pending, onSubmit, onCancel } = props;

  const { t } = useTranslation();
  const formValidation: ChargePointFormValidation =
    useChargePointFormValidation();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { touchedFields, errors, isValid },
  } = useForm<ChargePointFormModel>({
    mode: 'onChange',
    defaultValues: {
      ...defaultValues,
    },
  });

  const [ocpiCpo, setOcpiCpo] = useState<CpoModel | null>(null);
  const [ocpiLocation, setOcpiLocation] = useState<LocationModel | null>(null);
  const [ocpiEvse, setOcpiEvse] = useState<EvseModel | null>(null);

  const qrCodeIdentifier = useWatch({ control, name: 'qrCodeIdentifier' });

  useEffect(() => {
    if (qrCodeIdentifier) {
      setValue('mustGenerateQrCode', true);
    }
  }, [qrCodeIdentifier, setValue]);

  const chargePointTranslations: Record<keyof CreateChargePointModel, string> =
    t('model.charge-point', {
      returnObjects: true,
    });

  const handleSubmitOnlyDirtyField = (payload: ChargePointFormModel) => {
    onSubmit(removeEmptyValues(payload) as ChargePointFormModel);
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleSubmitOnlyDirtyField)}>
      <DialogContent>
        <Controller
          control={control}
          name="cpoId"
          rules={formValidation.cpoId}
          render={({ field: { onChange } }) => (
            <CpoSelect
              label={chargePointTranslations.cpoId}
              errorMessage={errors?.cpoId?.message}
              isTouched={touchedFields?.cpoId}
              onChange={(newValue) => {
                onChange(newValue?.id ?? null);
                setOcpiCpo(newValue ?? null);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="locationId"
          rules={formValidation.locationId}
          render={({ field: { onChange } }) => (
            <LocationSelect
              cpoId={ocpiCpo?.id}
              label={chargePointTranslations.locationId}
              errorMessage={errors?.locationId?.message}
              isTouched={touchedFields?.locationId}
              onChange={(newValue) => {
                onChange(newValue?.id ?? null);
                setOcpiLocation(newValue ?? null);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="evseUid"
          rules={formValidation.evseUid}
          render={({ field: { onChange } }) => (
            <EvseSelect
              evseList={ocpiLocation?.evses ?? []}
              label={chargePointTranslations.evseUid}
              errorMessage={errors?.evseUid?.message}
              isTouched={touchedFields?.evseUid}
              onChange={(newValue) => {
                onChange(newValue?.uid ?? null);
                setOcpiEvse(newValue ?? null);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="connectorId"
          rules={formValidation.connectorId}
          render={({ field: { onChange } }) => (
            <ConnectorSelect
              connectorList={ocpiEvse?.connectors ?? []}
              label={chargePointTranslations.connectorId}
              errorMessage={errors?.connectorId?.message}
              isTouched={touchedFields?.connectorId}
              onChange={(newValue) => {
                onChange(newValue?.id ?? null);
              }}
            />
          )}
        />

        <TextField
          {...register('comment', formValidation.comment)}
          label={chargePointTranslations.comment}
          errorMessage={errors?.comment?.message}
          isTouched={touchedFields?.comment}
        />

        <TextField
          {...register('themeId', formValidation.themeId)}
          label={chargePointTranslations.themeId}
          errorMessage={errors?.themeId?.message}
          isTouched={touchedFields?.themeId}
        />

        <TextField
          {...register('qrCodeIdentifier', formValidation.qrCodeIdentifier)}
          label={chargePointTranslations.qrCodeIdentifier}
          errorMessage={errors?.qrCodeIdentifier?.message}
          isTouched={touchedFields?.qrCodeIdentifier}
        />

        <Controller
          control={control}
          name="mustGenerateQrCode"
          rules={formValidation.mustGenerateQrCode}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              control={<Checkbox checked={!!value} />}
              onChange={(_e, checked) => onChange(checked)}
              label={chargePointTranslations.mustGenerateQrCode}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <CancelButton onClick={onCancel} />
        <SubmitButton loading={pending} disabled={!isValid}>
          {t(`form.${formType}-button`)}
        </SubmitButton>
      </DialogActions>
    </form>
  );
}
